<script lang="ts">
  import { run } from "svelte/legacy";
  import { melt } from "@melt-ui/svelte";
  import { createSlider } from "@melt-ui/svelte";

  interface Props {
    steps: number[];
    value: number;
    onValueChange?: (value: number) => void;
    disabled?: boolean;
    name?: string;
    id?: string;
    class?: string;
    formatLabel?: (value: number, index: number) => string | undefined;
    formatValue?: (value: number) => string;
  }

  let {
    steps,
    value,
    onValueChange,
    disabled = false,
    name,
    id,
    class: className = "",
    formatLabel,
    formatValue,
  }: Props = $props();

  function nearestIndex(val: number): number {
    let best = 0;
    let bestDist = Math.abs(steps[0] - val);
    for (let i = 1; i < steps.length; i++) {
      const dist = Math.abs(steps[i] - val);
      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    }
    return best;
  }

  let syncing = false;

  const {
    elements: { root, thumbs, range },
    states: { value: sliderValue },
    // svelte-ignore state_referenced_locally
  } = createSlider({
    defaultValue: [nearestIndex(value)],
    min: 0,
    max: steps.length - 1,
    step: 1,
    disabled,
    onValueChange: ({ curr, next }) => {
      if (!syncing) {
        onValueChange?.(steps[next[0]]);
      }
      return next;
    },
  });

  // Sync external value prop → slider index without triggering onValueChange
  run(() => {
    const idx = nearestIndex(value);
    if ($sliderValue[0] !== idx) {
      syncing = true;
      $sliderValue = [idx];
      syncing = false;
    }
  });
</script>

<div class="flex flex-col gap-1 {className}" {id}>
  <span {...$root} use:$root.action class="relative flex h-5 w-full items-center">
    <span class="absolute inset-x-0 h-1 rounded-full bg-mono-200"></span>
    <span {...$range} use:$range.action class="h-1 rounded-full bg-pri-800"></span>
    {#each $thumbs as thumb}
      <span
        {...thumb} use:thumb.action
        class="block h-4 w-4 rounded-full bg-mono-000 shadow-border-inset focus-ring
          {disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}"
      ></span>
    {/each}
  </span>

  <div class="relative flex w-full justify-between">
    {#each steps as stepVal, i}
      {@const isSelected = $sliderValue[0] === i}
      {@const staticLabel = formatLabel ? formatLabel(stepVal, i) : String(stepVal)}
      {@const selectedLabel =
        isSelected && !staticLabel && formatValue ? formatValue(stepVal) : undefined}
      {@const label = selectedLabel ?? staticLabel}
      <div class="flex w-0 flex-col items-center gap-0.5">
        <span class="h-2 w-px bg-mono-400"></span>
        {#if label}
          <span
            class="text-xs whitespace-nowrap {isSelected
              ? 'text-foreground-primary'
              : 'text-foreground-tertiary'}">{label}</span
          >
        {/if}
      </div>
    {/each}
  </div>

  {#if name}
    <input type="hidden" {name} value={steps[$sliderValue[0]]} />
  {/if}
</div>
